import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// A packing station (issue #305): a PC where each Label Size it prints is
// linked to the Zebra printer loaded with that roll (Warehouse › Packing
// Station). The links live on the PC itself, in this browser, since they
// describe the printers plugged into it; any other device isn't a packing
// station, so Pick Order offers it picking and Put Back only.
//
// Stored as { [label size id]: Browser Print device }. Label Size ids are
// unique across tenants, so one PC can serve more than one.

const STORAGE_KEY = 'optim.packingStation';
const CHANGE_EVENT = 'optim-packing-station-change';

export function readLinks() {
  try {
    const links = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    return links && typeof links === 'object' ? links : {};
  } catch {
    return {};
  }
}

function writeLinks(links) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(links));
  } catch {
    // Storage blocked: the link lasts for this page only.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function linkPrinter(sizeId, device) {
  const links = readLinks();
  if (device) links[sizeId] = device;
  else delete links[sizeId];
  writeLinks(links);
}

// The tenant's active Label Sizes and this PC's links to them. Until the
// sizes arrive, any link at all counts as a packing station, so Pick Order
// doesn't flicker between the two.
export function usePackingStation() {
  const [sizes, setSizes] = useState(null);
  const [links, setLinks] = useState(readLinks);

  useEffect(() => {
    let live = true;
    axios.get('/api/label-sizes')
      .then((res) => { if (live) setSizes(res.data.data || []); })
      .catch(() => { if (live) setSizes([]); });
    const refresh = () => setLinks(readLinks());
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      live = false;
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const printerFor = useCallback((sizeId) => links[sizeId] || null, [links]);
  const isStation = sizes
    ? sizes.some((s) => links[s.id])
    : Object.keys(links).length > 0;

  return { sizes: sizes || [], sizesLoaded: !!sizes, links, isStation, printerFor };
}
